import jstz from 'jstz';
import moment from 'moment-timezone';
import { DAILY, EVERY_5_MINUTES } from '../enums/EnumDashboardRefreshFrequency';

export function isoStringToJsDate(isoString) {
  if (!isoString) {
    return null;
  }
  return new Date(isoString);
}

export function currentTimezone() {
  return jstz.determine().name();
}

/**
 * 8am as ISO string
 */
export function morning8am() {
  const ts = new Date();
  ts.setHours(8);
  ts.setMinutes(0);
  ts.setSeconds(0);
  ts.setMilliseconds(0);
  return ts.toISOString();
}

//
// returns a timestamp in format HH:mm:ss
//
export function formatTime(utcTimestampString) {
  if (!utcTimestampString) {
    return null;
  }
  return formatTs(utcTimestampString, 'HH:mm:ss');
}

export function formatTimeAMPM(utcTimestampString) {
  const n = formatTime(utcTimestampString).split(':')[0];
  return hourToAmPm(n);
}

//
// takes hour as integer (0-23) and converts it to AM/PM format
// e.g. 11 AM
//
export function hourToAmPm(hour) {
  const amPm = hour < 12 ? 'AM' : 'PM';
  hour %= 12;
  if (hour === 0) {
    hour = 12;
  }
  return hour + amPm;
}

export function formatDate(utcTimestampString) {
  if (!utcTimestampString) {
    return null;
  }
  return formatTs(utcTimestampString, 'DD-MMM-YYYY');
}

function formatTs(utcTimestampString, format) {
  const currTz = currentTimezone();
  const momentTime = moment(utcTimestampString);
  const tzTime = momentTime.tz(currTz);
  const formattedDate = tzTime.format(format);
  return formattedDate;
}

/**
 * Given a timestamp string in yyyy-MM-dd'T'HH-mm-ss'Z' format,
 * this function will convert it to a localized timestamp
 */
export function formatTimestamp(utcTimestampString) {
  if (!utcTimestampString) {
    return null;
  }
  const momentTime = moment(utcTimestampString);
  const millisToNow = moment().diff(momentTime);
  const SECONDS = 1000;
  const MINUTES = SECONDS * 60;
  const HOURS = MINUTES * 60;
  const DAYS = HOURS * 24;
  const WEEKS = DAYS * 7;
  const MONTHS = DAYS * 30;
  let msg;
  if (millisToNow < MINUTES) {
    msg = 'just now';
  } else if (millisToNow < HOURS) {
    msg = `${Math.floor(millisToNow / MINUTES)} minute(s) ago`;
  } else if (millisToNow < DAYS) {
    msg = `${Math.floor(millisToNow / HOURS)} hour(s) ago`;
  } else if (millisToNow < WEEKS) {
    msg = `${Math.floor(millisToNow / DAYS)} day(s) ago`;
  } else if (millisToNow < MONTHS) {
    msg = `${Math.floor(millisToNow / WEEKS)} week(s) ago`;
  } else {
    msg = `${Math.floor(millisToNow / MONTHS)} month(s) ago`;
  }
  return msg; // + ' ('+formattedTime+') ';
}

export function getEndOfWeek(startDate) {
  const date = moment(startDate, ['YYYY-MM-DD', 'YYYY-MM-DD HH:mm']);
  return date.day(7).format('YYYY-MM-DD');
}

export function getStartOfWeek(startDate) {
  const date = moment(startDate, ['YYYY-MM-DD', 'YYYY-MM-DD HH:mm']);
  return date.day(1).format('YYYY-MM-DD');
}

export function formatValueToShow(value, filter) {
  if (!value) {
    return null;
  }
  let initialValue = moment(value, ['YYYY-MM-DD', 'YYYY-MM-DD HH:mm']);
  if (filter) {
    initialValue = initialValue.format(getFormatMoment(filter));
  } else {
    initialValue = initialValue.format('YYYY-MM-DD HH:mm');
  }
  return initialValue;
}

export function formatValueToSend(value, aggregation) {
  if (!value) {
    return null;
  }
  const initialValue = moment(value, ['YYYY-MM-DD', 'YYYY-MM-DD HH:mm']);
  return aggregation
    ? initialValue.format('YYYY-MM-DD')
    : initialValue.format('YYYY-MM-DD HH:mm');
}

export function getFormatMoment(filter) {
  switch (filter) {
    case 'year':
      return 'YYYY';
    case 'month':
      return 'YYYY-MM';
    case 'week':
    case 'quarter':
    case 'day':
      return 'YYYY-MM-DD';
    default:
      return 'YYYY-MM-DD hh:mm';
  }
}

// takes a dashboard as an argument
export function refreshFrequency(d) {
  let updateFrequency = d.refreshFrequency();
  if (updateFrequency === DAILY) {
    const time = d.time();
    const n = time.split(':')[0];
    const amPm = hourToAmPm(n);
    updateFrequency = `${d.refreshFrequency()} at ${amPm} / ${n}:00`;
  } else if (updateFrequency === EVERY_5_MINUTES) {
    updateFrequency = updateFrequency.replaceAll('_', ' ');
  }
  return updateFrequency;
}

export function showTime(timestamp) {
  const time = formatTime(timestamp);
  const n = time.split(':')[0];
  const amPm = hourToAmPm(n);
  return `${amPm} / ${n}:00`;
}

export const isDefaultDatetime = (dateString) => {
  return dateString?.includes('00:00:00.000+00:00');
};
